import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import type { OnApproveData, PayPalScriptOptions } from '@paypal/paypal-js';
import { Theme } from '@pmi.web/react-theme';
import {
  PayPalCheckout,
  PayPalCheckoutCreatePaymentOptions,
} from 'braintree-web';

import { registerStartupable } from '@/services/base/StartupHelper';
import { isProdBuild } from '@/utils/env';

import { BraintreeClient } from './BraintreeClient';
import { OptionalStartupable, StartupAuthContext } from '../base/Startupable';
import PaymentStore from '../payments/PaymentStore';
import { PayPalIdAlreadyUsedError } from './errors/PayPalIdAlreadyUsedError';
import { PayPalCheckoutInstanceMissingError } from './errors/PayPalInstanceMissingError';
import { createPayPalCheckoutInstance } from './helpers/BraintreeSdkWrapper';
import { PayPalSupportedLocale } from './helpers/BraintreeSupportedLocalesHelper';
import { FUNDING_SOURCE } from './schemas/BraintreeFundingSources';
import { PayPalBtnStyle } from './schemas/PayPalBtnStyle';
/**
 * TODO: replace this custom type with the fixed one from @types/braintree-web when available
 *
 * Custom type to override the currently incorrect PayPalCheckoutTokenizationOptions type definition
 * Corrections were suggested by PP team, more info is available by @bogdan.radacina
 * Using type assertion to bypass incorrect type definition from braintree-web package
 *
 * Fixed properties based on the suggestions by PayPal team :
 * - paymentId -> paymentID
 * - payerId -> payerID
 */
interface PayPalCheckoutTokenizationOptions {
  payerID: string;
  paymentID?: string | undefined;
  billingToken?: string | undefined;
  vault?: boolean | undefined;
}

class BraintreePayPalPayLaterService implements OptionalStartupable {
  public readonly name = 'BraintreePayPalPayLaterService';
  public readonly moduleId = PaymentModuleId.BraintreePayPalPayLater;

  private _checkoutInstance: PayPalCheckout | undefined;
  private _braintreeClient!: BraintreeClient;

  constructor() {
    registerStartupable(this, [PaymentStore]);
  }

  public async startup(
    _mode: unknown,
    authContext?: StartupAuthContext,
  ): Promise<void> {
    if (authContext === undefined) {
      throw new Error('Auth context is not set');
    }

    this.ensureCanInit();

    const paypalToken = await PaymentStore.getBraintreePaypalPayLaterToken();

    if (paypalToken === undefined) {
      throw new Error('Paypal token has no value!');
    }

    this._braintreeClient = new BraintreeClient(
      this.moduleId,
      paypalToken,
      authContext.accessToken,
    );
    await this._braintreeClient.init();

    this._checkoutInstance = await createPayPalCheckoutInstance(
      this._braintreeClient.client,
    );
  }

  public shouldStart(): boolean {
    const isAllowed = PaymentStore.allowedPaymentModules.includes(
      this.moduleId,
    );

    return isAllowed;
  }

  public createOrder(locale: SupportedLanguage) {
    if (!this._checkoutInstance) {
      throw new PayPalCheckoutInstanceMissingError();
    }

    const options: PayPalCheckoutCreatePaymentOptions = {
      enableShippingAddress: false,
      shippingAddressEditable: false,
      flow: 'checkout' as never, // The enum is not exported by the dependency
      intent: 'authorize' as never, // The enum is not exported by the dependency
      locale: PayPalSupportedLocale[locale],
      currency: PaymentStore.currency,
      amount: PaymentStore.orderTotal,
    };

    return this._checkoutInstance.createPayment(options);
  }

  public async getTokenizedPayloadNonce(data: OnApproveData) {
    if (!this._checkoutInstance) {
      throw new PayPalCheckoutInstanceMissingError();
    }

    const tokenizeOptions: PayPalCheckoutTokenizationOptions = {
      payerID: data.payerID ?? '',
      paymentID: data.paymentID ?? '',
      vault: false,
    };

    const tokenizedData = await this._checkoutInstance.tokenizePayment(
      tokenizeOptions as unknown as Parameters<
        typeof this._checkoutInstance.tokenizePayment
      >[0],
    );

    return tokenizedData.nonce;
  }

  public getPayPalBtnStyle(theme: Theme): PayPalBtnStyle {
    return {
      shape: 'pill',
      label: 'pay',
      layout: 'horizontal',
      tagline: false,
      height: 55,
      color: theme === Theme.FitLine ? 'gold' : 'blue',
    };
  }

  public getPayPalPayLaterScriptOptions(
    locale: SupportedLanguage,
  ): PayPalScriptOptions {
    let scriptOpts: PayPalScriptOptions = {
      clientId: PaymentStore.clientId ?? '',
      locale: PayPalSupportedLocale[locale],
      currency: PaymentStore.currency,
      components: 'buttons',
      enableFunding: [FUNDING_SOURCE.PAYLATER],
      disableFunding: [
        FUNDING_SOURCE.CARD,
        FUNDING_SOURCE.VENMO,
        FUNDING_SOURCE.SEPA,
        FUNDING_SOURCE.SOFORT,
        FUNDING_SOURCE.GIROPAY,
        FUNDING_SOURCE.EPS,
        FUNDING_SOURCE.P24,
        FUNDING_SOURCE.BLIK,
        FUNDING_SOURCE.MYBANK,
        FUNDING_SOURCE.BANCONTACT,
      ],
      debug: true,
      intent: 'authorize',
    };

    // For testing PayPal Pay Later in non-production environments,
    // we need to set the buyerCountry
    if (!isProdBuild) {
      scriptOpts = PaymentStore.customerCountryCode
        ? {
            ...scriptOpts,
            buyerCountry: PaymentStore.customerCountryCode.toUpperCase(),
          }
        : scriptOpts;
    }

    return scriptOpts;
  }

  public authorize(nonceToken?: string, instrumentId?: string) {
    return this._braintreeClient.authorize(
      PaymentStore.paymentIntentId,
      nonceToken,
      instrumentId,
    );
  }

  public async tokenize(
    nonceToken: string,
    customerNumber: string | undefined,
  ) {
    if (customerNumber === undefined) {
      throw new Error('Customer number is not set');
    }

    if (PaymentStore.legalEntity === undefined) {
      throw new Error('Legal entity cannot be undefined');
    }

    await this._braintreeClient.tokenize(
      PaymentStore.legalEntity,
      customerNumber,
      nonceToken,
    );

    return Promise.resolve();
  }

  private ensureCanInit() {
    const domElement = document.getElementById('paypal') ?? undefined;

    if (domElement !== undefined) {
      throw new PayPalIdAlreadyUsedError();
    }
  }
}

export default new BraintreePayPalPayLaterService();
